/**
 * Financial Reports — Phase 6.
 *
 * Pure selectors over GL state. No writes, no side effects.
 * Three statements:
 *   • Income Statement (P&L) — by trade + operating expense roll-up
 *   • Balance Sheet — Assets = Liabilities + Equity (incl. period net income)
 *   • Cash Flow — direct method, derived from cash-account movements
 *
 * Periods are yyyy-mm strings; "all" means lifetime.
 */
import type { GLAccount, JournalEntry } from "../glTypes";

export type PeriodKey = string; // "2026-06" or "all"

const norm = (acc: GLAccount, debit: number, credit: number): number =>
  acc.normalBalance === "debit" ? debit - credit : credit - debit;

interface Totals { debit: number; credit: number }

function totalsByAccount(
  journalEntries: JournalEntry[],
  period: PeriodKey,
): Map<string, Totals> {
  const out = new Map<string, Totals>();
  const add = (id: string, side: "debit" | "credit", amt: number) => {
    const t = out.get(id) ?? { debit: 0, credit: 0 };
    if (side === "debit") t.debit += amt;
    else t.credit += amt;
    out.set(id, t);
  };
  for (const je of journalEntries) {
    if (je.status !== "posted") continue;
    if (period !== "all" && je.period !== period) continue;
    for (const l of je.lines) add(l.accountId, l.side, l.amount);
  }
  return out;
}

/** Cumulative totals up to and including the given period (for Balance Sheet). */
function totalsThroughPeriod(
  journalEntries: JournalEntry[],
  period: PeriodKey,
): Map<string, Totals> {
  const out = new Map<string, Totals>();
  const add = (id: string, side: "debit" | "credit", amt: number) => {
    const t = out.get(id) ?? { debit: 0, credit: 0 };
    if (side === "debit") t.debit += amt;
    else t.credit += amt;
    out.set(id, t);
  };
  for (const je of journalEntries) {
    if (je.status !== "posted") continue;
    if (period !== "all" && je.period > period) continue;
    for (const l of je.lines) add(l.accountId, l.side, l.amount);
  }
  return out;
}

// ─────────────────────────────────────────────────────────────
// Income Statement
// ─────────────────────────────────────────────────────────────

export interface PLAccountRow {
  accountId: string;
  number: string;
  name: string;
  amount: number;
  tradeType?: string;
}

export interface PLTradeBlock {
  tradeType: string;
  revenue: number;
  costOfMaterial: number;
  costOfLabor: number;
  grossProfit: number;
  grossMarginPct: number;
}

export interface IncomeStatement {
  period: PeriodKey;
  revenueRows: PLAccountRow[];
  cogsMaterialRows: PLAccountRow[];
  cogsLaborRows: PLAccountRow[];
  opexRows: PLAccountRow[];
  byTrade: PLTradeBlock[];
  totalRevenue: number;
  totalCogs: number;
  grossProfit: number;
  grossMarginPct: number;
  totalOpex: number;
  netIncome: number;
  netMarginPct: number;
}

export function computeIncomeStatement(
  accounts: GLAccount[],
  journalEntries: JournalEntry[],
  period: PeriodKey,
): IncomeStatement {
  const tot = totalsByAccount(journalEntries, period);
  const row = (a: GLAccount): PLAccountRow => {
    const t = tot.get(a.id) ?? { debit: 0, credit: 0 };
    return {
      accountId: a.id,
      number: a.number,
      name: a.name,
      amount: norm(a, t.debit, t.credit),
      tradeType: a.tradeType,
    };
  };

  const revenueRows = accounts
    .filter(a => a.type === "revenue" && a.id !== "4000" && a.active)
    .map(row)
    .filter(r => r.amount !== 0);
  const cogsMaterialRows = accounts
    .filter(a => a.subtype === "cost_of_material" && a.id !== "5000" && a.active)
    .map(row)
    .filter(r => r.amount !== 0);
  const cogsLaborRows = accounts
    .filter(a => a.subtype === "cost_of_labor" && a.id !== "5500" && a.active)
    .map(row)
    .filter(r => r.amount !== 0);
  const opexRows = accounts
    .filter(a => a.subtype === "operating_expense" && a.active)
    .map(row)
    .filter(r => r.amount !== 0);

  // Trade roll-up
  const tradeMap = new Map<string, PLTradeBlock>();
  const ensure = (trade: string): PLTradeBlock => {
    let b = tradeMap.get(trade);
    if (!b) {
      b = { tradeType: trade, revenue: 0, costOfMaterial: 0, costOfLabor: 0, grossProfit: 0, grossMarginPct: 0 };
      tradeMap.set(trade, b);
    }
    return b;
  };
  for (const r of revenueRows) if (r.tradeType) ensure(r.tradeType).revenue += r.amount;
  for (const r of cogsMaterialRows) if (r.tradeType) ensure(r.tradeType).costOfMaterial += r.amount;
  for (const r of cogsLaborRows) if (r.tradeType) ensure(r.tradeType).costOfLabor += r.amount;
  const byTrade = Array.from(tradeMap.values()).map(b => {
    const gp = b.revenue - b.costOfMaterial - b.costOfLabor;
    return {
      ...b,
      grossProfit: gp,
      grossMarginPct: b.revenue > 0 ? (gp / b.revenue) * 100 : 0,
    };
  }).sort((a, b) => b.revenue - a.revenue);

  const totalRevenue = revenueRows.reduce((s, r) => s + r.amount, 0);
  const totalCogs =
    cogsMaterialRows.reduce((s, r) => s + r.amount, 0) +
    cogsLaborRows.reduce((s, r) => s + r.amount, 0);
  const grossProfit = totalRevenue - totalCogs;
  const totalOpex = opexRows.reduce((s, r) => s + r.amount, 0);
  const netIncome = grossProfit - totalOpex;

  return {
    period,
    revenueRows,
    cogsMaterialRows,
    cogsLaborRows,
    opexRows,
    byTrade,
    totalRevenue,
    totalCogs,
    grossProfit,
    grossMarginPct: totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0,
    totalOpex,
    netIncome,
    netMarginPct: totalRevenue > 0 ? (netIncome / totalRevenue) * 100 : 0,
  };
}

// ─────────────────────────────────────────────────────────────
// Balance Sheet
// ─────────────────────────────────────────────────────────────

export interface BSAccountRow {
  accountId: string;
  number: string;
  name: string;
  balance: number;
}

export interface BalanceSheet {
  asOf: PeriodKey;
  assets: { rows: BSAccountRow[]; total: number };
  liabilities: { rows: BSAccountRow[]; total: number };
  equity: {
    rows: BSAccountRow[];
    netIncome: number;
    total: number;
  };
  liabilitiesAndEquity: number;
  inBalance: boolean;
}

export function computeBalanceSheet(
  accounts: GLAccount[],
  journalEntries: JournalEntry[],
  period: PeriodKey,
  /**
   * Closed periods whose net income has already been rolled to Retained Earnings.
   * Revenue/expense activity in these periods must NOT be re-added as current-period
   * net income — that would double-count equity. Pass periodLocks from the GL.
   */
  closedPeriods: Set<string> = new Set(),
): BalanceSheet {
  const tot = totalsThroughPeriod(journalEntries, period);
  const balanceOf = (a: GLAccount) => {
    const t = tot.get(a.id) ?? { debit: 0, credit: 0 };
    return norm(a, t.debit, t.credit);
  };
  const mkRow = (a: GLAccount): BSAccountRow => ({
    accountId: a.id, number: a.number, name: a.name, balance: balanceOf(a),
  });
  const assetRows = accounts.filter(a => a.type === "asset" && a.active).map(mkRow).filter(r => r.balance !== 0);
  const liabRows = accounts.filter(a => a.type === "liability" && a.active).map(mkRow).filter(r => r.balance !== 0);
  const equityRows = accounts.filter(a => a.type === "equity" && a.active).map(mkRow).filter(r => r.balance !== 0);

  // Current-period net income = revenue - expense for activity NOT already rolled
  // into Retained Earnings via a period close. Build totals from JEs whose period
  // is not in closedPeriods.
  const unrolled = new Map<string, Totals>();
  const accTypeById = new Map(accounts.map(a => [a.id, a]));
  for (const je of journalEntries) {
    if (je.status !== "posted") continue;
    if (period !== "all" && je.period > period) continue;
    if (closedPeriods.has(je.period)) continue;
    for (const l of je.lines) {
      const acc = accTypeById.get(l.accountId);
      if (!acc || (acc.type !== "revenue" && acc.type !== "expense")) continue;
      const t = unrolled.get(l.accountId) ?? { debit: 0, credit: 0 };
      if (l.side === "debit") t.debit += l.amount; else t.credit += l.amount;
      unrolled.set(l.accountId, t);
    }
  }
  let revenueSum = 0;
  let expenseSum = 0;
  for (const [id, t] of unrolled) {
    const a = accTypeById.get(id)!;
    if (a.type === "revenue") revenueSum += norm(a, t.debit, t.credit);
    if (a.type === "expense") expenseSum += norm(a, t.debit, t.credit);
  }
  const netIncome = revenueSum - expenseSum;

  const assetsTotal = assetRows.reduce((s, r) => s + r.balance, 0);
  const liabTotal = liabRows.reduce((s, r) => s + r.balance, 0);
  const equityTotal = equityRows.reduce((s, r) => s + r.balance, 0) + netIncome;
  const lae = liabTotal + equityTotal;

  return {
    asOf: period,
    assets: { rows: assetRows, total: assetsTotal },
    liabilities: { rows: liabRows, total: liabTotal },
    equity: { rows: equityRows, netIncome, total: equityTotal },
    liabilitiesAndEquity: lae,
    inBalance: Math.round(assetsTotal * 100) === Math.round(lae * 100),
  };
}

// ─────────────────────────────────────────────────────────────
// Cash Flow — direct method from cash account movements
// ─────────────────────────────────────────────────────────────

export interface CashFlowRow {
  label: string;
  amount: number; // + inflow, - outflow
  source: string;
}

export interface CashFlowStatement {
  period: PeriodKey;
  openingCash: number;
  operatingRows: CashFlowRow[];
  investingRows: CashFlowRow[];
  financingRows: CashFlowRow[];
  netCashChange: number;
  closingCash: number;
}

const CASH_SUBTYPES = new Set(["cash", "unreconciled_cash"]);
const INVESTING_SUBTYPES = new Set(["fixed_asset"]);
const FINANCING_SUBTYPES = new Set(["equity", "retained_earnings"]);

type Activity = "operating" | "investing" | "financing";

/**
 * Classify the non-cash side of a JE into an activity bucket:
 *   • investing  — non-cash leg touches a fixed asset (purchase/sale of PP&E)
 *   • financing  — non-cash leg touches owner equity or long-term debt
 *                  (capital contribution, draw, loan proceeds, loan repayment)
 *   • operating  — everything else (AR, AP, payroll, expenses, WIP)
 */
function classifyByCounterLegs(
  je: JournalEntry,
  accountById: Map<string, GLAccount>,
  cashAccountIds: Set<string>,
): Activity {
  let sawInvesting = false;
  let sawFinancing = false;
  for (const l of je.lines) {
    if (cashAccountIds.has(l.accountId)) continue;
    const acc = accountById.get(l.accountId);
    if (!acc) continue;
    if (INVESTING_SUBTYPES.has(acc.subtype as string)) sawInvesting = true;
    if (FINANCING_SUBTYPES.has(acc.subtype as string)) sawFinancing = true;
    // Long-term-debt heuristic: liability account whose name suggests a loan/note.
    if (acc.type === "liability") {
      const n = `${acc.name} ${acc.number}`.toLowerCase();
      if (n.includes("loan") || n.includes("note payable") || n.includes("long-term") || n.includes("mortgage")) {
        sawFinancing = true;
      }
    }
  }
  if (sawInvesting) return "investing";
  if (sawFinancing) return "financing";
  return "operating";
}

function sourceLabel(source: string): string {
  return {
    ar_invoice: "AR Invoice",
    ar_cash_receipt: "Customer Payment",
    ap_bill: "Vendor Bill",
    ap_payment: "Vendor Payment",
    payroll: "Payroll",
    wip_release: "WIP Release",
    bank_feed: "Bank Feed",
    adjustment: "Adjustment",
    manual: "Manual Entry",
  }[source] ?? source;
}

export function computeCashFlow(
  accounts: GLAccount[],
  journalEntries: JournalEntry[],
  period: PeriodKey,
): CashFlowStatement {
  const cashAccountIds = new Set(
    accounts.filter(a => CASH_SUBTYPES.has(a.subtype as string)).map(a => a.id),
  );
  const accountById = new Map(accounts.map(a => [a.id, a] as const));

  // Opening cash = cumulative net through prior period
  let openingCash = 0;
  if (period !== "all") {
    for (const je of journalEntries) {
      if (je.status !== "posted") continue;
      if (je.period >= period) continue;
      for (const l of je.lines) {
        if (!cashAccountIds.has(l.accountId)) continue;
        openingCash += l.side === "debit" ? l.amount : -l.amount;
      }
    }
  }

  // Aggregate by source within period
  const buckets = {
    operating: new Map<string, number>(),
    investing: new Map<string, number>(),
    financing: new Map<string, number>(),
  };
  for (const je of journalEntries) {
    if (je.status !== "posted") continue;
    if (period !== "all" && je.period !== period) continue;
    let cashDelta = 0;
    for (const l of je.lines) {
      if (!cashAccountIds.has(l.accountId)) continue;
      cashDelta += l.side === "debit" ? l.amount : -l.amount;
    }
    if (cashDelta === 0) continue;
    const activity = classifyByCounterLegs(je, accountById, cashAccountIds);
    const key = sourceLabel(je.source);
    const map = buckets[activity];
    map.set(key, (map.get(key) ?? 0) + cashDelta);
  }


  const toRows = (m: Map<string, number>): CashFlowRow[] =>
    Array.from(m.entries())
      .map(([label, amount]) => ({ label, amount, source: label }))
      .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));

  const operatingRows = toRows(buckets.operating);
  const investingRows = toRows(buckets.investing);
  const financingRows = toRows(buckets.financing);
  const netCashChange =
    operatingRows.reduce((s, r) => s + r.amount, 0) +
    investingRows.reduce((s, r) => s + r.amount, 0) +
    financingRows.reduce((s, r) => s + r.amount, 0);

  return {
    period,
    openingCash,
    operatingRows,
    investingRows,
    financingRows,
    netCashChange,
    closingCash: openingCash + netCashChange,
  };
}

// ─────────────────────────────────────────────────────────────
// Period helpers
// ─────────────────────────────────────────────────────────────

export function listAvailablePeriods(journalEntries: JournalEntry[]): PeriodKey[] {
  const set = new Set<PeriodKey>();
  for (const je of journalEntries) if (je.status === "posted") set.add(je.period);
  return Array.from(set).sort((a, b) => b.localeCompare(a));
}

export function formatPeriodLabel(p: PeriodKey): string {
  if (p === "all") return "All time";
  const [y, m] = p.split("-");
  const month = new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-US", { month: "long" });
  return `${month} ${y}`;
}
